import moment from "moment";
import TransactionCardSkeleton from "@/components/skeletons/transaction-card-skeleton";
import { useSheetStore } from "@/zustand/sheetStore";
import Pagination from "@/components/pagination";
import { useFormatAmountWithCurrency } from "@/hooks/use-format-with-currency";

const tabToTypeMap = {
  deposit: "Deposit",
  withdrawal: "Withdrawal",
  transfer: "Transfer",
  "card-topup": "Card Topup",
  "card-withdrawal": "Card Withdrawal",
  "card-creation": "Card Creation",
  "referral-reward": "Referral Reward",
};

const typeMap = {
  TransferIn: "Card Deposit",
  TransferOut: "Card Withdrawal",
  Fee_Consumption: "Fee",
};

const TransactionCard = ({ transaction }: { transaction: Transaction }) => {
  const formatAmount = useFormatAmountWithCurrency();
  const sign = transaction.type === "deposit" ? "+" : "-";
  const { openSheet } = useSheetStore();
  return (
    <div
      onClick={() =>
        openSheet("transaction-details", null, {
          transactionData: transaction,
          type: "wallet",
        })
      }
      className="space-y-1 rounded-lg border border-[#283968] bg-linear-(--gradient-card) p-3 text-white"
    >
      <div className="flex items-center justify-between text-sm">
        <span>{transaction.currency || "USD"}</span>
        <div>
          {sign}
          {formatAmount(transaction.amount)}
        </div>
      </div>
      <div className="text-text-neutral flex items-center justify-between text-[10px]">
        <span>{moment(transaction.createdAt).format("DD-MM-YY hh:mm A")}</span>
        <span>{tabToTypeMap[transaction?.type]}</span>
      </div>
      <div className="text-text-neutral flex items-center justify-between text-[10px]">
        <span>Id: {transaction?.id}</span>
        <StatusBadge status={transaction.status} />
      </div>
    </div>
  );
};

const CardTransactionCard = ({
  transaction,
}: {
  transaction: CardTransaction;
}) => {
  const { openSheet } = useSheetStore();
  const sign = transaction.type === "TransferIn" ? "+" : "-";
  const formatAmount = useFormatAmountWithCurrency();

  return (
    <div
      onClick={() =>
        openSheet("transaction-details", null, {
          transactionData: transaction,
          type: "card",
        })
      }
      className="space-y-1 rounded-lg border border-[#283968] bg-linear-(--gradient-card) p-3 text-white"
    >
      <div className="flex items-center justify-between text-sm">
        <span>
          {transaction.merchantName ||
            typeMap[transaction.type] ||
            transaction.type}
        </span>

        <div>
          {sign}
          {formatAmount(transaction.amount)}
        </div>
      </div>
      <div className="text-text-neutral flex items-center justify-between text-[10px]">
        <span>{moment(transaction.createTime).format("DD-MM-YY hh:mm A")}</span>
        <StatusBadge status={transaction.status} />
      </div>
    </div>
  );
};

const TransactionList = ({
  transactions,
  isLoading = false,
  type = "wallets",
  handlePageChange,
  totalPages,
  currentPage,
}: {
  transactions: Transaction[] | CardTransaction[];
  isLoading?: boolean;
  type?: "wallets" | "cards";
  handlePageChange?: (page: number) => void;
  totalPages?: number;
  currentPage?: number;
}) => {
  if (isLoading)
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <TransactionCardSkeleton key={index} />
        ))}
      </div>
    );

  if (transactions.length === 0)
    return (
      <div>
        <div className="py-20">
          <p className="text-center text-white">No transactions yet</p>
        </div>
        {type === "cards" && (
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalPages / 10) || 1}
            onPageChange={handlePageChange as (page: number) => void}
          />
        )}
      </div>
    );
  return (
    <div>
      <div className="space-y-4">
        {transactions.map((transaction) => {
          if (type === "wallets")
            return (
              <TransactionCard
                transaction={transaction as Transaction}
                key={transaction?.id}
              />
            );

          if (type === "cards")
            return (
              <CardTransactionCard
                transaction={transaction as CardTransaction}
                key={transaction?.id}
              />
            );
        })}
      </div>

      {type === "wallets" && totalPages && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages || 1}
          onPageChange={handlePageChange as (page: number) => void}
        />
      )}

      {type === "cards" && totalPages && (
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalPages / 10) - 1 || 1}
          onPageChange={handlePageChange as (page: number) => void}
        />
      )}
    </div>
  );
};

type Status =
  | "closed"
  | "pending"
  | "completed"
  | "failed"
  | "rejected"
  | "fail";

interface StatusBadgeProps {
  status: string;
}

const statusStyles: Record<
  Status,
  { text: string; bg: string; textColor: string }
> = {
  closed: {
    text: "Closed",
    bg: "bg-[#FFD25A]",
    textColor: "text-[#FFD25A]",
  },
  pending: {
    text: "Pending",
    bg: "bg-[#FFD25A]",
    textColor: "text-[#FFD25A]",
  },
  completed: {
    text: "Completed",
    bg: "bg-[#9BFFB7]",
    textColor: "text-[#9BFFB7]",
  },
  failed: {
    text: "Failed",
    bg: "bg-red-800",
    textColor: "text-red-800",
  },
  fail: {
    text: "Failed",
    bg: "bg-red-800",
    textColor: "text-red-800",
  },
  rejected: {
    text: "Rejected",
    bg: "bg-red-100",
    textColor: "text-red-800",
  },
};

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const { text, bg, textColor } =
    statusStyles[status?.toLowerCase() as keyof typeof statusStyles];

  return (
    <span
      className={`inline-flex items-center rounded-full py-1 text-[10px] ${textColor}`}
    >
      <span className={`mr-1 size-1 rounded-full ${bg}`} />
      {text}
    </span>
  );
};

export default TransactionList;
